import {
    Combobox,
    ComboboxCancel,
    ComboboxItemProps as AriakitComboboxItemProps,
    ComboboxPopoverProps,
    ComboboxProps,
    useComboboxContext,
    ComboboxItemCheck,
    useStoreState,
    ComboboxGroupLabelProps
} from '@ariakit/react'
import {
    StyledComboboxGroupLabel,
    StyledComboboxInputSideComponent,
    StyledComboboxInputText,
    StyledComboboxInputWrapper,
    StyledComboboxItem,
    StyledComboboxItemCheckboxWrapper,
    StyledComboboxPopover
} from './style'
import {InputTextProps} from '@/components/commons/input-text/InputText'
import {raise} from '@/utilities/helpers'
import {forwardRef, ReactNode} from 'react'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {useTheme} from 'styled-components'

export const ComboboxPopover = (props: ComboboxPopoverProps) => (
    <StyledComboboxPopover gutter={6} sameWidth {...props} />
)

export const ComboboxGroupLabel = ({children, ...props}: ComboboxGroupLabelProps) => (
    <StyledComboboxGroupLabel {...props}>{children}</StyledComboboxGroupLabel>
)

interface ComboboxItemProps extends AriakitComboboxItemProps {
    hasItemCheckbox?: boolean
}
export const ComboboxItem = ({hasItemCheckbox, ...props}: ComboboxItemProps) => (
    <StyledComboboxItem {...props}>
        {hasItemCheckbox && (
            <StyledComboboxItemCheckboxWrapper>
                <ComboboxItemCheck />
            </StyledComboboxItemCheckboxWrapper>
        )}
        {props.children}
    </StyledComboboxItem>
)

type ComboboxInputProps = ComboboxProps &
    Omit<InputTextProps, 'type' | 'touched' | 'visibilityToggle' | 'typeIcon'> & {
        onClear?: () => void
        hasCloseCombobox?: boolean
        isLoading?: boolean
        typeIcon?: ReactNode
        disabled?: boolean
    }

export const ComboboxInput = forwardRef<HTMLInputElement, ComboboxInputProps>(
    ({typeIcon, hasCloseCombobox, onClear, isLoading, disabled, inputSize, ...props}, ref) => {
        const comboboxContext = useComboboxContext() ?? raise('Combobox must be in a ComboboxProvider')
        const value = useStoreState(comboboxContext, 'value')
        const {palette} = useTheme()

        return (
            <StyledComboboxInputWrapper>
                {typeIcon && (
                    <StyledComboboxInputSideComponent $side="left">{typeIcon}</StyledComboboxInputSideComponent>
                )}
                <Combobox
                    autoSelect
                    render={
                        <StyledComboboxInputText
                            type="text"
                            ref={ref}
                            disabled={disabled}
                            $size={inputSize ?? 'md'}
                            $hasTypeIcon={!!typeIcon}
                            $hasCloseCombobox={!!hasCloseCombobox}
                            $hasError={!!props.errorMessage}
                        />
                    }
                    {...props}
                />
                {hasCloseCombobox && value && !isLoading && (
                    <StyledComboboxInputSideComponent $side="right">
                        <ComboboxCancel onClick={onClear} />
                    </StyledComboboxInputSideComponent>
                )}
                {isLoading && (
                    <StyledComboboxInputSideComponent $side="right">
                        <Spinner width={16} height={16} color={palette.neutral['400']} />
                    </StyledComboboxInputSideComponent>
                )}
            </StyledComboboxInputWrapper>
        )
    }
)
